/**
 * Kondo Integration component for displaying webhook URL and managing API keys
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link04, XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useClipboard } from '@/hooks/useClipboard';
import type {
  CreateTeamApiKeyResponse,
  TeamApiKey,
} from '@/lib/api/teamApiKeys';
import {
  createTeamApiKey,
  deleteTeamApiKey,
  listTeamApiKeys,
} from '@/lib/api/teamApiKeys';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { getApiBaseUrl } from '@/lib/config';
import { logger } from '@/lib/logger';

import { ConfigureKondoCard } from './kondo/ConfigureKondoCard';
import { ConnectedKondoKeyPanel } from './kondo/ConnectedKondoKeyPanel';

const KEY_NAME_TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function generateKondoKeyName(): string {
  const timestamp = new Date().toLocaleDateString(
    'en-US',
    KEY_NAME_TIMESTAMP_FORMAT
  );
  return `Kondo Integration (${timestamp})`;
}

export function KondoIntegration() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const queryClient = useQueryClient();

  const [isExpanded, setIsExpanded] = useState(false);
  const [newApiKey, setNewApiKey] = useState<CreateTeamApiKeyResponse | null>(
    null
  );
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const { copied, copy } = useClipboard();
  const webhookCopied = copied === 'webhook';
  const newKeyCopied = copied === 'key';

  // Query for API keys
  const {
    data: apiKeys = [],
    isLoading: isLoadingKeys,
    error: keysError,
  } = useQuery<TeamApiKey[]>({
    queryKey: ['teamApiKeys', orgId],
    queryFn: ({ signal }) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return listTeamApiKeys(orgId, signal);
    },
    enabled: !!orgId,
  });

  useEffect(() => {
    if (keysError) {
      logger.error('Failed to load Kondo API keys', keysError, { orgId });
    }
  }, [keysError, orgId]);

  // Filter to only Kondo-related API keys
  const kondoApiKey = useMemo(() => {
    return apiKeys.find(key => key.name.toLowerCase().includes('kondo'));
  }, [apiKeys]);

  const isConnected = !!kondoApiKey;

  // Clear org-scoped state when orgId changes
  useEffect(() => {
    setNewApiKey(null);
    setMutationError(null);
    setClipboardError(null);
    setIsConfirmOpen(false);
    setIsRegenerateConfirmOpen(false);
  }, [orgId]);

  // Auto-expand when connected (only reacts to connection changes)
  useEffect(() => {
    if (isConnected) {
      setIsExpanded(true);
    }
  }, [isConnected]);

  // Mutation for creating API key
  const createKeyMutation = useMutation({
    mutationFn: (name: string) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return createTeamApiKey(orgId, name);
    },
    onSuccess: data => {
      setNewApiKey(data);
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
    },
    onError: err => {
      logger.error(
        'Failed to connect Kondo integration',
        err instanceof Error ? err : undefined,
        { orgId }
      );
      setMutationError("We couldn't connect Kondo. Try again.");
    },
  });

  // Mutation for deleting API key
  const deleteKeyMutation = useMutation({
    mutationFn: (keyId: string) => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return deleteTeamApiKey(orgId, keyId);
    },
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
      setIsExpanded(false);
    },
    onError: () => {
      setMutationError("We couldn't disconnect Kondo. Try again.");
    },
  });

  // Mutation for regenerating API key (delete old, create new)
  const regenerateKeyMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      if (!kondoApiKey) {
        throw new Error('No existing Kondo API key to regenerate');
      }
      await deleteTeamApiKey(orgId, kondoApiKey.id);
      return createTeamApiKey(orgId, generateKondoKeyName());
    },
    onSuccess: data => {
      setNewApiKey(data);
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
    },
    onError: () => {
      // The old key was already deleted before creation was attempted, so the
      // cached ['teamApiKeys'] list is now stale (still shows the old key as
      // connected). Invalidate to refetch actual server state and clear any
      // previously revealed key so the UI reflects the real integration status.
      setNewApiKey(null);
      queryClient.invalidateQueries({ queryKey: ['teamApiKeys', orgId] });
      setMutationError("We couldn't regenerate the key. Try again.");
    },
  });

  const apiBase = getApiBaseUrl();
  const webhookUrl = orgId
    ? `${apiBase || window.location.origin}/api/orgs/${orgId}/webhooks/v1/kondo/`
    : '';

  const handleCopyWebhook = useCallback(async () => {
    const result = await copy(webhookUrl, 'webhook');
    setClipboardError(
      result.success ? null : 'Copy failed – please select and copy manually.'
    );
  }, [copy, webhookUrl]);

  const handleCopyNewKey = useCallback(async () => {
    if (!newApiKey) {
      return;
    }
    const result = await copy(newApiKey.key, 'key');
    setClipboardError(
      result.success ? null : 'Copy failed – please select and copy manually.'
    );
  }, [copy, newApiKey]);

  const handleConnect = useCallback(() => {
    setMutationError(null);
    createKeyMutation.mutate(generateKondoKeyName());
    setIsExpanded(true);
  }, [createKeyMutation]);

  const handleDisconnectClick = useCallback(() => setIsConfirmOpen(true), []);
  const handleConfirmDisconnect = useCallback(async () => {
    setMutationError(null);
    if (kondoApiKey) {
      try {
        await deleteKeyMutation.mutateAsync(kondoApiKey.id);
      } catch (err) {
        logger.error(
          'Failed to disconnect Kondo integration',
          err instanceof Error ? err : undefined,
          { orgId, keyId: kondoApiKey.id }
        );
      }
    }
    setIsConfirmOpen(false);
  }, [kondoApiKey, deleteKeyMutation, orgId]);
  const handleCancelDisconnect = useCallback(() => setIsConfirmOpen(false), []);

  const handleRegenerateClick = useCallback(
    () => setIsRegenerateConfirmOpen(true),
    []
  );
  const handleConfirmRegenerate = useCallback(async () => {
    setMutationError(null);
    try {
      await regenerateKeyMutation.mutateAsync();
    } catch (err) {
      logger.error(
        'Failed to regenerate Kondo API key',
        err instanceof Error ? err : undefined,
        { orgId, keyId: kondoApiKey?.id }
      );
    }
    setIsRegenerateConfirmOpen(false);
  }, [regenerateKeyMutation, orgId, kondoApiKey]);
  const handleCancelRegenerate = useCallback(
    () => setIsRegenerateConfirmOpen(false),
    []
  );

  const handleDismissMutationError = useCallback(
    () => setMutationError(null),
    []
  );
  const handleDismissClipboardError = useCallback(
    () => setClipboardError(null),
    []
  );

  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-border-secondary'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon icon={Link04} size='sm' theme='modern' color='gray' />
          <div className='flex-1'>
            <h2 className='text-lg font-semibold text-secondary'>Kondo</h2>
            <p className='text-sm text-tertiary mt-0.5'>
              Sync LinkedIn conversations from Kondo
            </p>
          </div>
          {(() => {
            if (isLoadingKeys) {
              return null;
            }
            if (keysError) {
              return (
                <Badge type='color' size='sm' color='error'>
                  Couldn't load status
                </Badge>
              );
            }
            if (isConnected) {
              return (
                <Badge type='color' size='sm' color='success'>
                  Connected
                </Badge>
              );
            }
            return (
              <Button
                color='primary'
                size='sm'
                onClick={handleConnect}
                isLoading={createKeyMutation.isPending}
              >
                Connect
              </Button>
            );
          })()}
        </div>
      </div>

      {isExpanded && (
        <div className='p-6 space-y-6'>
          {/* Mutation error */}
          {mutationError && (
            <div className='bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
              <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
              <p className='text-sm text-utility-error-700'>{mutationError}</p>
              <button
                type='button'
                onClick={handleDismissMutationError}
                className='ml-auto text-utility-error-600 hover:text-utility-error-700'
                aria-label='Dismiss'
              >
                <XCircle className='w-4 h-4' />
              </button>
            </div>
          )}

          {/* Clipboard error */}
          {clipboardError && (
            <div className='bg-utility-error-50 p-3 rounded-lg border border-utility-error-200 flex items-center gap-2'>
              <XCircle className='w-4 h-4 text-utility-error-600 shrink-0' />
              <p className='text-sm text-utility-error-700'>{clipboardError}</p>
              <button
                type='button'
                onClick={handleDismissClipboardError}
                className='ml-auto text-utility-error-600 hover:text-utility-error-700'
                aria-label='Dismiss'
              >
                <XCircle className='w-4 h-4' />
              </button>
            </div>
          )}

          {/* Connected State */}
          {isConnected && kondoApiKey && (
            <ConnectedKondoKeyPanel
              kondoApiKey={kondoApiKey}
              onRegenerateClick={handleRegenerateClick}
              isRegeneratePending={regenerateKeyMutation.isPending}
              onDisconnectClick={handleDisconnectClick}
              isDisconnectPending={deleteKeyMutation.isPending}
            />
          )}

          {/* Configure Kondo */}
          <ConfigureKondoCard
            webhookUrl={webhookUrl}
            webhookCopied={webhookCopied}
            onCopyWebhook={handleCopyWebhook}
            newApiKey={newApiKey}
            newKeyCopied={newKeyCopied}
            onCopyNewKey={handleCopyNewKey}
            isConnected={isConnected}
          />

          {/* What this enables */}
          <div className='bg-bg-secondary p-4 rounded-lg border border-border-secondary'>
            <h4 className='font-semibold text-primary mb-2 text-sm'>
              {isConnected ? 'Active features:' : "What you'll get:"}
            </h4>
            <ul className='list-disc list-inside text-sm space-y-1 text-tertiary'>
              <li>
                <strong className='text-primary'>
                  LinkedIn conversations:
                </strong>{' '}
                Automatically sync conversations from Kondo to contact timelines
              </li>
              <li>
                <strong className='text-primary'>Contact creation:</strong> New
                contacts created automatically from LinkedIn profiles
              </li>
            </ul>
          </div>
        </div>
      )}
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        variant='warning'
        title='Disconnect Kondo'
        description='LinkedIn conversations will no longer sync to Ahoy. You can reconnect any time.'
        confirmLabel='Disconnect'
        isLoading={deleteKeyMutation.isPending}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
      <ConfirmationDialog
        isOpen={isRegenerateConfirmOpen}
        variant='warning'
        title='Regenerate API key'
        description="Your current Kondo key will stop working immediately. You will need to paste the new key into Kondo's API Key field. Continue?"
        confirmLabel='Regenerate'
        isLoading={regenerateKeyMutation.isPending}
        onConfirm={handleConfirmRegenerate}
        onCancel={handleCancelRegenerate}
      />
    </div>
  );
}
